/**
 * npub.cash API client for Cashu token claiming and mint registration.
 *
 * npub.cash is a service that holds Cashu tokens for Nostr users. When
 * someone sends sats to a user's npub.cash address, the tokens are held
 * until the recipient claims them using NIP-98 authenticated requests.
 *
 * This module provides three operations:
 * 1. Claim pending tokens (GET /api/v1/claim)
 * 2. Register preferred mint (PUT /api/v1/info/mint)
 * 3. Claim vanity username (PUT /api/v1/info/username) — two-step flow
 *
 * The vanity username flow uses PUT /api/v1/info/username for both
 * availability checking and claiming. A 402 response means "available,
 * pay to claim." A 400 with "already taken" means unavailable.
 *
 * SECURITY: Private keys are passed as Uint8Array and never logged.
 * Auth tokens are generated per-request and not cached.
 *
 * Tier: All tiers (infrastructure for Cashu receive via npub.cash).
 *
 * @module npub-cash
 */

import { createNip98AuthHeader } from './nostr/nip98';

/** Base URL for the npub.cash API. */
export const NPUB_CASH_BASE_URL = 'https://npub.cash';

/** Minimum username length for vanity addresses. */
export const MIN_USERNAME_LENGTH = 3;

/** Regex pattern for valid vanity usernames: alphanumeric, cannot start with "npub1". */
export const USERNAME_PATTERN = /^(?!npub1)[a-zA-Z0-9]+$/;

/** Cost of claiming a vanity username in satoshis. */
export const VANITY_USERNAME_COST_SATS = 5_000;

/**
 * Response shape from the npub.cash claim endpoint.
 * The API returns a single token string in data.token, plus count and totalPending.
 */
interface ClaimResponse {
  error?: boolean;
  data?: {
    token?: string;
    count?: number;
    totalPending?: number;
  };
}

/**
 * Claim pending Cashu tokens from npub.cash.
 *
 * Sends a NIP-98 authenticated GET request to retrieve any tokens
 * that have been sent to the user's npub.cash address. Returns an
 * array of Cashu token strings (cashuA... or cashuB...).
 *
 * The API returns a single token string in data.token. We wrap it
 * in an array for backward compatibility with consumers.
 *
 * This is best-effort: on any error, returns an empty array. The
 * caller is expected to retry via periodic polling.
 *
 * @param privkey - The user's 32-byte Nostr private key for NIP-98 auth
 * @returns Array of Cashu token strings (may be empty)
 */
export async function claimNpubCashTokens(privkey: Uint8Array): Promise<string[]> {
  const url = `${NPUB_CASH_BASE_URL}/api/v1/claim`;

  try {
    const authHeader = await createNip98AuthHeader(url, 'GET', privkey);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: authHeader,
      },
    });

    if (!response.ok) {
      return [];
    }

    const json = (await response.json()) as ClaimResponse;

    // API returns a single token string in data.token
    if (json.data?.token && typeof json.data.token === 'string') {
      return [json.data.token];
    }

    return [];
  } catch {
    // Network errors, JSON parse errors, etc. -- claim is best-effort
    return [];
  }
}

/**
 * Register the user's preferred Cashu mint with npub.cash.
 *
 * Tells npub.cash which mint to use when creating tokens for this user.
 * This is typically called once during wallet setup or when the user
 * changes their preferred mint.
 *
 * The API returns 204 No Content on success.
 *
 * @param privkey - The user's 32-byte Nostr private key for NIP-98 auth
 * @param mintUrl - The URL of the preferred Cashu mint
 * @returns true on success (2xx response), false on any failure
 */
export async function registerNpubCashMint(privkey: Uint8Array, mintUrl: string): Promise<boolean> {
  const url = `${NPUB_CASH_BASE_URL}/api/v1/info/mint`;

  try {
    const authHeader = await createNip98AuthHeader(url, 'PUT', privkey, { mintUrl });

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mintUrl }),
    });

    return response.ok;
  } catch {
    return false;
  }
}

// ── Vanity Username API ─────────────────────────────────────────────────────

/** Result from a vanity username claim attempt. */
export interface UsernameClaimResult {
  readonly status: 'available' | 'taken' | 'already_set' | 'payment_required' | 'claimed' | 'error';
  /** BOLT11 invoice to pay (when status is 'payment_required'). */
  readonly invoice?: string;
  /** JWT payment token (when status is 'payment_required'). */
  readonly paymentToken?: string;
  /** Error description (when status is 'error', 'taken', or 'already_set'). */
  readonly error?: string;
}

/**
 * Validate a vanity username locally before making API calls.
 *
 * Rules: at least 3 characters, alphanumeric only (no underscores),
 * cannot start with "npub1".
 *
 * @param username - The desired username
 * @returns Error message if invalid, or null if valid
 */
export function validateUsername(username: string): string | null {
  if (username.length < MIN_USERNAME_LENGTH) {
    return `Username must be at least ${MIN_USERNAME_LENGTH} characters`;
  }
  if (!USERNAME_PATTERN.test(username)) {
    return 'Username can only contain letters and numbers';
  }
  return null;
}

/** Shape of a 402 response from the username claim endpoint. */
interface UsernamePaymentResponse {
  error?: boolean;
  message?: string;
  data?: {
    paymentToken?: string;
    paymentRequest?: string;
  };
}

/** Shape of an error response from the username claim endpoint. */
interface UsernameErrorResponse {
  error?: boolean;
  message?: string;
}

/**
 * Check if a vanity username is available on npub.cash.
 *
 * There is no separate availability endpoint. This calls PUT /api/v1/info/username
 * and interprets the response:
 * - 402 = available (payment required to claim)
 * - 400 + "already taken" = taken
 * - 400 + "already set" = user already has a username
 *
 * @param username - The desired username to check
 * @param privkey - The user's 32-byte Nostr private key for NIP-98 auth
 * @returns Claim result indicating availability
 */
export async function checkUsernameAvailability(
  username: string,
  privkey: Uint8Array,
): Promise<UsernameClaimResult> {
  const validationError = validateUsername(username);
  if (validationError) {
    return { status: 'error', error: validationError };
  }

  const url = `${NPUB_CASH_BASE_URL}/api/v1/info/username`;
  const body = { username };

  try {
    const authHeader = await createNip98AuthHeader(url, 'PUT', privkey, body);

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.status === 402) {
      // Available — 402 means "pay to claim"
      const json = (await response.json()) as UsernamePaymentResponse;
      if (json.data?.paymentRequest && json.data?.paymentToken) {
        return {
          status: 'available',
          invoice: json.data.paymentRequest,
          paymentToken: json.data.paymentToken,
        };
      }
      return { status: 'error', error: 'Payment required but no invoice received' };
    }

    if (response.status === 400) {
      const json = (await response.json()) as UsernameErrorResponse;
      const msg = json.message ?? '';
      if (msg.toLowerCase().includes('already taken')) {
        return { status: 'taken', error: 'Username is already taken' };
      }
      if (msg.toLowerCase().includes('already set')) {
        return { status: 'already_set', error: 'You already have a username' };
      }
      return { status: 'error', error: msg || `Unexpected error (400)` };
    }

    if (response.status === 200) {
      // 200 without step 2 means it was claimed (shouldn't happen for availability check)
      return { status: 'claimed' };
    }

    return { status: 'error', error: `Unexpected response (${response.status})` };
  } catch {
    return { status: 'error', error: 'Network error — please try again' };
  }
}

/**
 * Claim a vanity username on npub.cash (step 2 — payment confirmation).
 *
 * After checking availability (which returns a paymentToken and invoice),
 * the user pays the invoice and then calls this to confirm the claim.
 *
 * Both steps use NIP-98 auth. The paymentToken goes in the request body
 * (NOT as a Bearer header).
 *
 * SECURITY: JWT token is never logged. NIP-98 auth generated per-request.
 *
 * @param username - The desired username
 * @param privkey - The user's 32-byte Nostr private key for NIP-98 auth
 * @param paymentToken - JWT from the 402 response (step 1)
 * @returns Claim result with status
 */
export async function claimVanityUsername(
  username: string,
  privkey: Uint8Array,
  paymentToken: string,
): Promise<UsernameClaimResult> {
  const validationError = validateUsername(username);
  if (validationError) {
    return { status: 'error', error: validationError };
  }

  const url = `${NPUB_CASH_BASE_URL}/api/v1/info/username`;
  const body = { username, paymentToken };

  try {
    // Step 2 also uses NIP-98 auth (not Bearer)
    const authHeader = await createNip98AuthHeader(url, 'PUT', privkey, body);

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.status === 200) {
      return { status: 'claimed' };
    }

    if (response.status === 400) {
      const json = (await response.json()) as UsernameErrorResponse;
      const msg = json.message ?? '';
      if (msg.toLowerCase().includes('already taken')) {
        return { status: 'taken', error: 'Username was taken while you were paying' };
      }
      return { status: 'error', error: msg || 'Claim failed (400)' };
    }

    // Surface server error message for unexpected statuses
    try {
      const json = (await response.json()) as UsernameErrorResponse;
      const msg = json.message ?? '';
      return { status: 'error', error: msg || `Claim failed (${response.status})` };
    } catch {
      return { status: 'error', error: `Claim failed (${response.status})` };
    }
  } catch {
    return { status: 'error', error: 'Network error — please try again' };
  }
}
